import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, ChevronRight, Calendar, BookOpen, CheckSquare, KeyRound, LogOut, Settings, RefreshCw, Mail, Newspaper, Volume2, Loader2, X, Award } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";
import { FlightsCard, refreshFlights } from "@/components/dashboard/FlightsCard";
import { TripsCard } from "@/components/dashboard/TripsCard";
import { formatEventTitle, isCancelledEvent } from "@/lib/utils";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { EventDetailModal } from "@/components/EventDetailModal";
import { GmailMessageModal } from "@/components/GmailMessageModal";
import { StudyPlanModal } from "@/components/StudyPlanModal";
import { InstallHint } from "@/components/InstallHint";
import { JarvisCard } from "@/components/dashboard/JarvisCard";
import { SchoolHoursCard } from "@/components/dashboard/SchoolHoursCard";
import { ContinueListeningCard } from "@/components/dashboard/ContinueListeningCard";
import { FootballInlineScore } from "@/components/dashboard/FootballInlineScore";
import { Section } from "@/components/dashboard/Section";
import { HomeworkSection, StudyPlanCard } from "@/components/dashboard/HomeworkSection";
import { QuizStatsCard } from "@/components/dashboard/QuizStatsCard";
import { playQuizWord, primeQuizAudio } from "@/lib/quizAudio";

const JARVIS_IMG = "https://static.prod-images.emergentagent.com/jobs/47440676-455b-415d-a656-32c4bba91c19/images/e7c5bee9ca26687529352ae6a149086b13e9babdbdee8f65009e93b70ca1b38d.png";

const weekday = (d) =>
  ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"][d.getDay()];

const WEEKDAYS_SV = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];

const formatDateLong = (d) => {
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${WEEKDAYS_SV[d.getDay()]} ${d.getDate()} ${m[d.getMonth()]}`;
};

const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
};

const sameDay = (a, b) => a.toDateString() === b.toDateString();

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [homework, setHomework] = useState([]);
  const [eventModalId, setEventModalId] = useState(null);
  const [gmailModalId, setGmailModalId] = useState(null);
  const [studyPlans, setStudyPlans] = useState([]);
  const [studyPlanOpen, setStudyPlanOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [tomorrowSchool, setTomorrowSchool] = useState([]);
  const [unread, setUnread] = useState([]);
  const [feedCount, setFeedCount] = useState(null);
  const [jarvis, setJarvis] = useState("");
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]); // [{role:"user"|"assistant", text}]
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  // Edit pokes — aktivt poke om inaktiv >24h
  const [activePoke, setActivePoke] = useState(null);

  useEffect(() => {
    api.get("/jarvis/poke/active")
      .then(({ data }) => setActivePoke(data.poke || null))
      .catch(() => {});
  }, []);

  // Om URL har ?poke=xxx (från push-länk), markera direkt som seen och scrolla till chat
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pokeId = params.get("poke");
    if (pokeId && activePoke?.id === pokeId) {
      api.post(`/jarvis/poke/${pokeId}/seen`).catch(() => {});
      // Skicka poke-meddelandet som första assistant-bubbla
      setChatHistory((h) => [
        ...h,
        { role: "assistant", text: activePoke.message, from_poke: true },
      ]);
      // Rensa query-param
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [activePoke]);

  const dismissPoke = async () => {
    if (!activePoke) return;
    try {
      await api.post(`/jarvis/poke/${activePoke.id}/dismiss`);
    } catch (_) {}
    setActivePoke(null);
  };

  const acceptPoke = () => {
    if (!activePoke) return;
    setChatHistory((h) => [...h, { role: "assistant", text: activePoke.message, from_poke: true }]);
    api.post(`/jarvis/poke/${activePoke.id}/seen`).catch(() => {});
    setActivePoke(null);
    focusJarvisCard();
  };

  const chatBottomRef = useRef(null);
  const jarvisCardRef = useRef(null);
  const today = new Date();

  // ----- Edit röst (TTS) -----
  const VOICES = [
    { id: "nova", label: "Nova", desc: "Varm & vänlig (kvinnlig)" },
    { id: "alloy", label: "Alloy", desc: "Neutral, energisk" },
    { id: "shimmer", label: "Shimmer", desc: "Mjuk & klar (kvinnlig)" },
    { id: "onyx", label: "Onyx", desc: "Djup & lugn (manlig)" },
    { id: "echo", label: "Echo", desc: "Tydlig & saklig (manlig)" },
    { id: "fable", label: "Fable", desc: "Brittisk accent, berättande" },
  ];
  const [voice, setVoice] = useState(
    () => localStorage.getItem("jarvis_voice") || "nova"
  );
  const [autoSpeakReplies, setAutoSpeakReplies] = useState(
    () => localStorage.getItem("jarvis_auto_speak") === "1"
  );
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const [loadingTtsKey, setLoadingTtsKey] = useState(null);
  const audioRef = useRef(null);
  const lastFromMicRef = useRef(false);

  useEffect(() => {
    localStorage.setItem("jarvis_voice", voice);
  }, [voice]);
  useEffect(() => {
    localStorage.setItem("jarvis_auto_speak", autoSpeakReplies ? "1" : "0");
  }, [autoSpeakReplies]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setPlayingKey(null);
  };

  const speakText = async (key, text) => {
    if (playingKey === key) { stopAudio(); return; }
    stopAudio();

    const audio = new Audio();
    audio.preload = "auto";
    audioRef.current = audio;
    setLoadingTtsKey(key);

    try {
      const { data, headers } = await api.post(
        "/jarvis/voice/speak",
        { text, voice },
        { responseType: "blob" }
      );
      // Sanity-check content-type
      const ct = (headers?.["content-type"] || data?.type || "").toLowerCase();
      if (!ct.startsWith("audio/")) {
        let detail = `Servern svarade med ${ct || "okänt format"}`;
        try {
          const txt = await data.text();
          if (txt && txt.length < 300) detail = txt;
        } catch (_) {}
        throw new Error(detail);
      }
      if (!(data instanceof Blob) || data.size < 100) {
        throw new Error("Tom audio-respons");
      }
      const url = URL.createObjectURL(data);

      // Bind handlers EFTER src är satt så de inte triggar på tidig state
      audio.onended = () => {
        setPlayingKey(null);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        // audio.error.code: 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
        const errCode = audio.error?.code;
        const codeMap = { 1: "avbruten", 2: "nätverksfel", 3: "kunde inte avkoda", 4: "format stöds ej" };
        console.error("[Edit TTS] audio error:", audio.error, "size:", data.size, "type:", data.type);
        setPlayingKey(null);
        URL.revokeObjectURL(url);
        toast.error(`Audio-fel (${codeMap[errCode] || "okänt"})`);
      };
      audio.src = url;
      setPlayingKey(key);

      try {
        await audio.play();
      } catch (playErr) {
        console.error("[Edit TTS] play() rejected:", playErr.name, playErr.message);
        setPlayingKey(null);
        URL.revokeObjectURL(url);
        if (playErr.name === "NotAllowedError") {
          toast.error("Tryck på högtalar-ikonen igen — browsern kräver klick för att låsa upp ljud.");
        } else {
          toast.error(`Uppspelningsfel: ${playErr.message || playErr.name}`);
        }
      }
    } catch (e) {
      console.error("[Edit TTS] fetch fel:", e);
      const detail = e?.response?.data?.detail || e?.message || "okänt fel";
      toast.error(`Kunde inte hämta ljud: ${detail}`);
    } finally {
      setLoadingTtsKey(null);
    }
  };

  // Auto-spela senaste assistant-svar när auto-speak är på.
  // Spårar lastSpokenIdx för att undvika dubbel-uppläsning vid re-renders.
  const lastSpokenIdxRef = useRef(-1);
  useEffect(() => {
    if (!autoSpeakReplies) return;
    const lastIdx = chatHistory.length - 1;
    if (lastIdx < 0 || lastIdx === lastSpokenIdxRef.current) return;
    const last = chatHistory[lastIdx];
    if (last?.role !== "assistant" || !last.text) return;
    lastSpokenIdxRef.current = lastIdx;
    speakText(`chat-${lastIdx}`, last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory, autoSpeakReplies]);

  // Scrolla Edit-kortet till toppen så hela vyn syns (briefing + history + input).
  // Anropas när input får fokus eller när nytt meddelande skickas.
  const focusJarvisCard = () => {
    setTimeout(() => {
      jarvisCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const load = async () => {
    try {
      const [u, hw, td, sp] = await Promise.all([
        api.get("/events/upcoming?limit=20&days=7"),
        api.get("/homework?upcoming=true&limit=200"),
        api.get("/todos?include_done=false"),
        api.get("/study-plan").catch(() => ({ data: { plans: [] } })),
      ]);
      setUpcoming(u.data);
      setHomework(hw.data);
      setTodos(td.data.slice(0, 4));
      setStudyPlans(sp.data.plans || []);
    } catch (e) {
      // no feeds yet is ok
    }
    // Morgondagens skoltider → visas som aktivitet under "I morgon"
    try {
      const t = new Date();
      t.setDate(t.getDate() + 1);
      const { data } = await api.get(`/school-hours/today?date=${t.toISOString().slice(0, 10)}`);
      setTomorrowSchool(data.children || []);
    } catch (_) {}
    // What's new — visa bara antalet OLÄSTA. Om inget nytt → 0 (göm badge).
    try {
      const { data } = await api.get("/feed/whats-new?limit=1");
      setFeedCount(data.new_count || 0);
    } catch (_) {}
    // Gmail unread (separate, soft-fail)
    try {
      const status = await api.get("/google/status");
      if (status.data?.connected) {
        const { data } = await api.get("/google/gmail/unread?max_results=3");
        setUnread(data.messages || []);
      }
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  const askJarvis = async () => {
    setJarvisLoading(true);
    try {
      const { data } = await api.post("/jarvis/summary");
      setJarvis(data.reply);
    } catch (e) {
      toast.error("Edit kunde inte svara just nu");
    } finally {
      setJarvisLoading(false);
    }
  };

  const sendMessage = async (msg) => {
    msg = (msg || "").trim();
    if (!msg || chatSending) return;
    primeQuizAudio();
    setChatHistory((h) => [...h, { role: "user", text: msg }]);
    setChatInput("");
    setChatSending(true);
    focusJarvisCard();
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    try {
      const doPost = () => api.post("/jarvis/chat", {
        message: msg,
        session_id: chatSessionId || undefined,
      }, { timeout: 180_000 });
      let data;
      try {
        ({ data } = await doPost());
      } catch (first) {
        // Auto-retry endast vid nätverksfel/omstart — aldrig timeout (undvik dubbla ändringar)
        const st = first?.response?.status;
        if (first?.code === "ECONNABORTED" || (st && ![502, 503, 504].includes(st))) throw first;
        await new Promise((r) => setTimeout(r, 3000));
        ({ data } = await doPost());
      }
      if (data.session_id) setChatSessionId(data.session_id);
      if (data.tts_word) playQuizWord(data.tts_word);
      setChatHistory((h) => [...h, {
        role: "assistant",
        text: data.reply || "(inget svar)",
        ttsWord: data.tts_word || null,
        sources: data.sources || [],
        web_used: data.web_used || false,
      }]);
      // Om Edit manipulerade flight-bevakningar → uppdatera dashboard direkt
      if (data.tool === "watch_flight" || data.tool === "unwatch_flight"
          || data.tool === "share_flight_watch") {
        refreshFlights();
      }
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch (err) {
      setChatHistory((h) => [
        ...h,
        { role: "assistant", text: "Edit kunde inte svara just nu. Försök igen om en stund." },
      ]);
    } finally {
      setChatSending(false);
    }
  };

  const sendChat = async (e) => {
    e?.preventDefault();
    await sendMessage(chatInput);
  };

  // "Förhör mig"-knapp från Läxor-sidan: /?quiz=<meddelande> → skicka direkt i chatten
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const quizMsg = params.get("quiz");
    if (quizMsg) {
      window.history.replaceState({}, "", window.location.pathname);
      focusJarvisCard();
      sendMessage(quizMsg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chatFileRef = useRef(null);
  const [chatUploading, setChatUploading] = useState(false);
  const handleChatFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max 25 MB per fil");
      return;
    }
    const note = chatInput.trim();
    setChatInput("");
    setChatHistory((h) => [...h, { role: "user", text: `📎 ${file.name}${note ? `\n${note}` : ""}` }]);
    setChatUploading(true);
    setChatSending(true);
    focusJarvisCard();
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (chatSessionId) fd.append("session_id", chatSessionId);
      if (note) fd.append("note", note);
      const { data } = await api.post("/jarvis/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      if (data.session_id) setChatSessionId(data.session_id);
      setChatHistory((h) => [...h, { role: "assistant", text: data.reply || "(inget svar)" }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "okänt fel";
      setChatHistory((h) => [...h, { role: "assistant", text: `Kunde inte läsa filen: ${detail}` }]);
    } finally {
      setChatUploading(false);
      setChatSending(false);
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    }
  };

  const refreshFeeds = async () => {
    try {
      await api.post("/feeds/refresh-all");
      toast.success("Kalendrar uppdaterade");
      load();
    } catch (e) {
      toast.error("Uppdatering misslyckades");
    }
  };

  const handleVoiceTranscript = (text, payload) => {
    const routed = payload?.routed;
    lastFromMicRef.current = true;
    focusJarvisCard();
    // Visa alltid vad användaren sa i chatten
    setChatHistory((h) => [...h, { role: "user", text }]);

    if (!routed) {
      // Fallback: behandla som chat-fråga
      setChatInput(text);
      setTimeout(() => sendChat(), 50);
      return;
    }

    const kind = routed.kind;
    const confirmation = routed.confirmation || "";

    if (kind === "question") {
      const ar = routed.action_result || {};
      if (ar.session_id) setChatSessionId(ar.session_id);
      setChatHistory((h) => [...h, {
        role: "assistant",
        text: confirmation,
        sources: ar.sources || [],
        web_used: ar.web_used || false,
      }]);
      return;
    }

    const emoji = kind === "todo" ? "✅" : kind === "memory" ? "🏠" :
                  kind === "code" ? "🔑" : kind === "briefing" ? "📧" : "❓";
    setChatHistory((h) => [...h, {
      role: "assistant",
      text: `${emoji} ${confirmation}`,
    }]);
    if (routed.ok) {
      const label = kind === "todo" ? "Påminnelse skapad" : kind === "memory" ? "Sparat i minne" :
                    kind === "code" ? "Kod sparad" : kind === "briefing" ? "Briefing köad" : "Klart";
      toast.success(`${emoji} ${label}`);
      // Ladda om dashboardens data så ev. ny todo syns
      if (kind === "todo" || kind === "code") {
        load();
      }
    } else {
      toast.error(confirmation);
    }
  };

  return (
    <div className="px-5 pt-3" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="label-eyebrow">{weekday(today)} · {formatDateLong(today)}</p>
          <h1 className="font-display text-3xl font-medium mt-1" data-testid="dashboard-greeting">
            Hej {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshFeeds}
            className="p-2 rounded-full hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
            data-testid="refresh-feeds-button"
            title="Uppdatera kalendrar"
          >
            <RefreshCw size={18} />
          </button>
          <NotificationsBell />
          <Link
            to="/installningar"
            className={`p-2 rounded-full hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] ${user?.role !== "admin" ? "hidden" : ""}`}
            data-testid="settings-button"
          >
            <Settings size={18} />
          </Link>
          <button
            onClick={logout}
            className="p-2 rounded-full hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
            data-testid="logout-button"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Diskret installations-tips (ingen popup, visas bara om appen inte är installerad) */}
      <InstallHint />

      {/* Edit Poke — visas om Edit vill ta kontakt (barn inaktivt >24h) */}
      {activePoke && (
        <div
          className="rounded-2xl p-4 mb-4 fade-up relative animate-pulse-soft"
          style={{
            background: "linear-gradient(135deg, rgba(130,160,188,0.22) 0%, rgba(130,160,188,0.08) 100%)",
            border: "2px solid rgba(130,160,188,0.5)",
          }}
          data-testid="jarvis-poke-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[color:var(--jarvis)] text-white flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)] mb-1">
                💭 Edit vill säga något
              </p>
              <p className="text-sm text-[color:var(--text-primary)] leading-relaxed mb-3">
                {activePoke.message}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={acceptPoke}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-[color:var(--jarvis)] text-white hover:opacity-90"
                  data-testid="jarvis-poke-accept"
                >
                  Svara Edit
                </button>
                <button
                  onClick={dismissPoke}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-white text-[color:var(--text-secondary)] border border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)]"
                  data-testid="jarvis-poke-dismiss"
                >
                  Senare
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit card — ALLTID ÖVERST: snabb summering + chatfönster (inline). */}
      <JarvisCard
        jarvisCardRef={jarvisCardRef}
        chatBottomRef={chatBottomRef}
        autoSpeakReplies={autoSpeakReplies}
        setAutoSpeakReplies={setAutoSpeakReplies}
        voicePickerOpen={voicePickerOpen}
        setVoicePickerOpen={setVoicePickerOpen}
        VOICES={VOICES}
        voice={voice}
        jarvis={jarvis}
        jarvisLoading={jarvisLoading}
        askJarvis={askJarvis}
        speakText={speakText}
        loadingTtsKey={loadingTtsKey}
        playingKey={playingKey}
        chatHistory={chatHistory}
        chatSending={chatSending}
        chatInput={chatInput}
        setChatInput={setChatInput}
        sendChat={sendChat}
        chatFileRef={chatFileRef}
        chatUploading={chatUploading}
        handleChatFileUpload={handleChatFileUpload}
        handleVoiceTranscript={handleVoiceTranscript}
        focusJarvisCard={focusJarvisCard}
        onFlightClick={(fid, dateHint) => {
          const msg = dateHint ? `bevaka ${fid} ${dateHint}` : `bevaka ${fid}`;
          sendMessage(msg);
        }}
      />

      {/* Skoltider idag — kompakt rad per barn (start–slut). */}
      <SchoolHoursCard />

      {/* Fortsätt lyssna — material där eleven är mitt i en uppspelning */}
      <ContinueListeningCard onOpenEvent={setEventModalId} />
      <TripsCard />
      <FlightsCard />

      {/* Förhörsstatistik per barn — endast föräldrar, visas när minst ett förhör gjorts */}
      {!user?.is_child && <QuizStatsCard />}

      {/* Provresultat hero card */}
      <Link
        to="/resultat"
        className="card-soft p-4 mb-4 flex items-center gap-3 hover:bg-[color:var(--bg-elevated)] transition-colors fade-up"
        data-testid="results-link"
      >
        <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <Award size={19} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Provresultat</p>
          <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
            Lärarnas bedömningar från Vklass
          </p>
        </div>
        <ChevronRight size={18} className="text-[color:var(--text-tertiary)]" />
      </Link>

      {/* What's new hero card */}
      <Link
        to="/vad-ar-nytt"
        className="card-soft p-4 mb-4 flex items-center gap-3 hover:bg-[color:var(--bg-elevated)] transition-colors fade-up"
        data-testid="whats-new-hero-card"
      >
        <div className="w-11 h-11 rounded-xl bg-[color:var(--moss)] text-white flex items-center justify-center flex-shrink-0">
          <Newspaper size={19} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Vad är nytt</p>
          <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
            {feedCount === null
              ? "Skola, mejl och kalender på ett ställe"
              : feedCount === 0
              ? "Inget nytt — bra jobbat 🌿"
              : `${feedCount} ${feedCount === 1 ? "post" : "poster"} att kolla`}
          </p>
        </div>
        {feedCount > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--moss)] text-white font-semibold">
            {feedCount}
          </span>
        )}
        <ChevronRight size={18} className="text-[color:var(--text-tertiary)]" />
      </Link>

      {/* Today & upcoming */}
      {(() => {
        // Bygg unified-listor: events + homework (dedup på id), sorterat på start.
        const byId = new Map();
        const pushItem = (e, kind) => {
          if (!e?.id || byId.has(e.id)) return;
          byId.set(e.id, { ...e, _kind: kind });
        };
        upcoming.forEach((e) => pushItem(e, "event"));
        homework.forEach((e) => pushItem(e, e.category === "test" ? "test" : e.category === "assignment" ? "assignment" : "homework"));
        const all = Array.from(byId.values()).sort((a, b) => (a.start || "").localeCompare(b.start || ""));
        const now = new Date();
        const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
        const startOfTomorrow = new Date(endOfToday); startOfTomorrow.setMilliseconds(startOfTomorrow.getMilliseconds() + 1);
        const endOfTomorrow = new Date(startOfTomorrow); endOfTomorrow.setHours(23, 59, 59, 999);
        const horizon7 = new Date(now); horizon7.setDate(horizon7.getDate() + 7);
        const todayItems = all.filter((e) => {
          try {
            const d = new Date(e.start);
            return d >= startOfToday && d <= endOfToday;
          } catch { return false; }
        });
        const tomorrowItems = all.filter((e) => {
          try {
            const d = new Date(e.start);
            return d >= startOfTomorrow && d <= endOfTomorrow;
          } catch { return false; }
        });
        const next7Items = all.filter((e) => {
          try {
            const d = new Date(e.start);
            return d > endOfTomorrow && d <= horizon7;
          } catch { return false; }
        });

        const renderRow = (e) => {
          const d = new Date(e.start);
          const kind = e._kind;
          const cancelled = kind === "event" && isCancelledEvent(e);
          const badge = kind === "test" ? "Prov" : kind === "homework" ? "Läxa" : kind === "assignment" ? "Uppgift" : null;
          const badgeColor = kind === "test"
            ? "bg-[#FDF2EE] text-[#E07A5F]"
            : kind === "homework"
            ? "bg-[#F0F6E6] text-[color:var(--moss)]"
            : kind === "assignment"
            ? "bg-[#E7F4F1] text-[#2A9D8F]"
            : null;
          const isClickable = kind === "test" || kind === "homework" || kind === "assignment";
          return (
            <li
              key={e.id}
              className={`card-soft p-3.5 fade-up flex items-center gap-3 ${isClickable ? "cursor-pointer hover:bg-[color:var(--bg-elevated)] transition-colors" : ""}`}
              data-testid={`dashboard-item-${e.id}`}
              onClick={isClickable ? () => setEventModalId(e.id) : undefined}
            >
              {badge ? (
                <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${badgeColor}`}>
                  {badge}
                </div>
              ) : (
                <div className="w-1.5 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: e.feed_color }} />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${cancelled ? "line-through text-[color:var(--text-tertiary)]" : "text-[color:var(--text-primary)]"}`}>
                  {formatEventTitle(e)}
                </p>
                <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                  {sameDay(d, today) ? `Idag · ${WEEKDAYS_SV[d.getDay()]}` : formatDateLong(d)} {!e.all_day && `· ${formatTime(e.start)}`}
                  {e.feed_owner && ` · ${e.feed_owner}`}
                </p>
                {e.minfotboll_game_id && <FootballInlineScore eventId={e.id} />}
              </div>
            </li>
          );
        };

        return (
          <>
            <Section
              title="Idag"
              to="/kalender"
              empty={todayItems.length === 0 ? "Inget kvar idag." : null}
              testid="today-section"
            >
              <ul className="space-y-2.5 stagger">{todayItems.map(renderRow)}</ul>
            </Section>
            <Section
              title="I morgon"
              to="/kalender"
              empty={tomorrowItems.length === 0 && tomorrowSchool.length === 0 ? "Inget inplanerat imorgon." : null}
              testid="tomorrow-section"
            >
              <ul className="space-y-2.5 stagger">
                {tomorrowSchool.map((c) => (
                  <li
                    key={`school-${c.child}`}
                    className="card-soft p-3.5 fade-up flex items-center gap-3"
                    data-testid={`tomorrow-school-${c.child}`}
                  >
                    <div className="w-1.5 h-10 rounded-full flex-shrink-0 bg-[#3D5A80]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[color:var(--text-primary)] truncate">
                        Skola, {c.start}–{c.end}
                      </p>
                      <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                        {c.child} · {c.lesson_count} lektioner
                      </p>
                    </div>
                  </li>
                ))}
                {tomorrowItems.map(renderRow)}
              </ul>
            </Section>
            <Section
              title="Kommande 7 dagar"
              to="/kalender"
              empty={next7Items.length === 0 ? "Inga kommande händelser." : null}
              testid="upcoming-section"
            >
              <ul className="space-y-2.5 stagger">{next7Items.map(renderRow)}</ul>
            </Section>
          </>
        );
      })()}

      {/* Studieschema-kort — visas om plan finns, klick öppnar modal */}
      <StudyPlanCard studyPlans={studyPlans} setStudyPlanOpen={setStudyPlanOpen} />


      {/* Inbox preview */}
      {unread.length > 0 && (
        <Section title="Inkorg" testid="inbox-section">
          <ul className="space-y-2 stagger">
            {unread.map((m) => (
              <li
                key={m.id}
                className="card-soft p-3.5 fade-up"
                data-testid={`inbox-item-${m.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#FDF2EE] text-[#E07A5F] flex items-center justify-center flex-shrink-0">
                    <Mail size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.subject}</p>
                    <p className="text-xs text-[color:var(--text-tertiary)] truncate mt-0.5">{m.from}</p>
                    <p className="text-xs text-[color:var(--text-secondary)] mt-1 line-clamp-2">{m.snippet}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Todos preview */}
      <Section
        title="Att-göra"
        to="/att-gora"
        empty={todos.length === 0 ? "Inga aktiva uppgifter just nu." : null}
        testid="todos-section"
      >
        <ul className="space-y-2 stagger">
          {todos.map((t) => (
            <li
              key={t.id}
              className="card-soft p-3.5 flex items-center gap-3 fade-up"
              data-testid={`todo-preview-${t.id}`}
            >
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  t.priority === "high" ? "bg-[#E07A5F]" : t.priority === "medium" ? "bg-[#F6CA83]" : "bg-[color:var(--sage)]"
                }`}
              />
              <span className="text-sm flex-1 truncate">{t.title}</span>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
                {t.list_type === "shared" ? "Gemensam" : "Personlig"}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Quick action to vault */}
      <Link
        to="/koder"
        className="card-soft p-4 flex items-center justify-between fade-up mt-5 hover:bg-[color:var(--bg-elevated)] transition-colors"
        data-testid="vault-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F4F3ED] flex items-center justify-center">
            <KeyRound size={18} className="text-[color:var(--moss)]" />
          </div>
          <div>
            <p className="text-sm font-medium">Viktiga koder</p>
            <p className="text-xs text-[color:var(--text-secondary)]">Portkod, Wi-Fi, försäkring…</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-[color:var(--text-tertiary)]" />
      </Link>

      {eventModalId && (
        <EventDetailModal
          eventId={eventModalId}
          onClose={() => setEventModalId(null)}
          onChanged={load}
          onOpenGmail={(id) => setGmailModalId(id)}
        />
      )}
      {gmailModalId && (
        <GmailMessageModal
          messageId={gmailModalId}
          onClose={() => setGmailModalId(null)}
        />
      )}
      {studyPlanOpen && (
        <StudyPlanModal onClose={() => { setStudyPlanOpen(false); load(); }} />
      )}

      {/* Röstväljar-popup — portal direkt till body så ingen overflow-hidden
          parent kan klippa den. */}
      {voicePickerOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6"
            onClick={() => setVoicePickerOpen(false)}
            data-testid="jarvis-voice-popup-backdrop"
          >
            <div
              className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="jarvis-voice-menu"
            >
              <header className="px-5 py-4 border-b border-[color:var(--border)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Volume2 size={16} className="text-[color:var(--jarvis)]" />
                  <h3 className="text-sm font-semibold">Välj röst för Edit</h3>
                </div>
                <button
                  onClick={() => setVoicePickerOpen(false)}
                  className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)]"
                  aria-label="Stäng"
                  data-testid="jarvis-voice-close"
                >
                  <X size={16} />
                </button>
              </header>
              <div className="overflow-y-auto p-2">
                {VOICES.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => {
                      setVoice(v.id);
                      speakText(`preview-${v.id}`, `Hej, jag är ${v.label}.`);
                    }}
                    className={`w-full text-left px-3 py-3 rounded-xl flex items-center gap-3 transition-colors ${
                      voice === v.id
                        ? "bg-[color:var(--jarvis)]/8 border border-[color:var(--jarvis)]/30"
                        : "hover:bg-[color:var(--bg-elevated)] border border-transparent"
                    }`}
                    data-testid={`jarvis-voice-${v.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      voice === v.id ? "bg-[color:var(--jarvis)] text-white" : "bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]"
                    }`}>
                      <Volume2 size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{v.label}</p>
                      <p className="text-[11px] text-[color:var(--text-tertiary)]">{v.desc}</p>
                    </div>
                    {playingKey === `preview-${v.id}` && (
                      <Loader2 size={14} className="animate-spin text-[color:var(--jarvis)]" />
                    )}
                    {voice === v.id && playingKey !== `preview-${v.id}` && (
                      <span className="text-[10px] font-semibold text-[color:var(--jarvis)] uppercase tracking-wider">
                        Vald
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <footer className="px-5 py-3 border-t border-[color:var(--border)]">
                <p className="text-[11px] text-[color:var(--text-tertiary)] leading-snug">
                  Tryck på en röst för att höra hur den låter. Valet sparas automatiskt.
                </p>
              </footer>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div className="card-soft p-3 fade-up" data-testid={testid}>
    <Icon size={16} className="text-[color:var(--text-tertiary)]" />
    <p className="font-display text-2xl font-medium mt-1.5">{value}</p>
    <p className="text-[11px] text-[color:var(--text-secondary)] uppercase tracking-wider">{label}</p>
  </div>
);
